import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trash2, Pencil, Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AdjustmentWithMonths,
  AdjustmentMonth,
} from "@shared-core/data/adapter";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";

// ── FY month grid ────────────────────────────────────────────────
//
// The grid columns mirror the chart's grid-cols-12 above. Keeping the
// month tuple here lets us turn typed values back into AdjustmentMonth
// rows without leaking calendar math into the parent.

export const FY_MONTHS: { year: number; month: number }[] = [
  { year: 2026, month: 4 },
  { year: 2026, month: 5 },
  { year: 2026, month: 6 },
  { year: 2026, month: 7 },
  { year: 2026, month: 8 },
  { year: 2026, month: 9 },
  { year: 2026, month: 10 },
  { year: 2026, month: 11 },
  { year: 2026, month: 12 },
  { year: 2027, month: 1 },
  { year: 2027, month: 2 },
  { year: 2027, month: 3 },
];

// Pure cell parse/format helpers extracted to adjustment-cell-format.ts so
// they're testable without pulling React. Re-export here so the old
// import paths still work for external consumers.
import {
  monthKey,
  fmtCellDisplay,
  parseCellInput,
} from "./adjustment-cell-format";
export { monthKey, fmtCellDisplay, parseCellInput };

interface Props {
  submissionId: string;
  scopeKind: "bucket" | "rep";
  scopeRefId: string;
  scopeLabel?: string;
  monthLabels: string[];
  adjustments: AdjustmentWithMonths[];
  bucketDefaults?: {
    contributionPct?: number;
    bookingToInvoiceDays?: number;
  };
  onChanged?: () => void;
}

export function AdjustmentGrid({
  submissionId,
  scopeKind,
  scopeRefId,
  scopeLabel,
  monthLabels,
  adjustments,
  bucketDefaults,
  onChanged,
}: Props) {
  const adapter = useDataAdapter();
  const [savingRowIds, setSavingRowIds] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  // After "+ Add adjustment" creates the row, focus its first month cell
  // once the parent reload delivers it — the row is born ready to type.
  const pendingFocusAdjId = useRef<string | null>(null);

  useEffect(() => {
    const id = pendingFocusAdjId.current;
    if (!id || !adjustments.some((a) => a.id === id)) return;
    pendingFocusAdjId.current = null;
    gridRef.current
      ?.querySelector<HTMLElement>(
        `[data-adj-id="${id}"] [data-cell-button="true"][data-cell-idx="0"]`,
      )
      ?.focus();
  }, [adjustments]);

  const markSaving = useCallback((id: string, on: boolean) => {
    setSavingRowIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const rowsById = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const adj of adjustments) {
      const inner = new Map<string, number>();
      for (const m of adj.months) {
        inner.set(monthKey(m.year, m.month), m.bookedContributionAmtGbp);
      }
      map.set(adj.id, inner);
    }
    return map;
  }, [adjustments]);

  const commitCell = useCallback(
    async (
      adj: AdjustmentWithMonths,
      year: number,
      month: number,
      raw: string,
    ) => {
      const newAmt = parseCellInput(raw);
      const existing = rowsById.get(adj.id);
      const prior = existing?.get(monthKey(year, month)) ?? 0;
      if (newAmt === prior) return;

      const updated: AdjustmentMonth[] = FY_MONTHS.map(
        ({ year: y, month: mo }) => {
          const k = monthKey(y, mo);
          if (y === year && mo === month) {
            return { year: y, month: mo, bookedContributionAmtGbp: newAmt };
          }
          const v = existing?.get(k) ?? 0;
          return { year: y, month: mo, bookedContributionAmtGbp: v };
        },
      ).filter((m) => m.bookedContributionAmtGbp !== 0);

      markSaving(adj.id, true);
      try {
        await adapter.updateAdjustment(adj.id, { months: updated });
        onChanged?.();
      } finally {
        markSaving(adj.id, false);
      }
    },
    [adapter, markSaving, onChanged, rowsById],
  );

  // "+ Add adjustment" persists a defaulted row immediately — no form
  // step. The row lands via the parent reload with its first month cell
  // focused (see the pendingFocusAdjId effect above); title and the
  // margin/lead overrides are inline-editable in place afterwards.
  const handleAddRow = useCallback(async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const created = await adapter.createAdjustment(submissionId, {
        scopeKind,
        scopeRefId,
        reason: "New adjustment",
        contributionPct: null,
        bookingToInvoiceTimeDays: null,
        months: [],
      });
      pendingFocusAdjId.current = created.id;
      onChanged?.();
    } catch (err) {
      // Surface the API failure inline — most common cause is that the
      // selected submission is `live` rather than a draft.
      const msg = err instanceof Error ? err.message : String(err);
      setCreateError(msg);
    } finally {
      setIsCreating(false);
    }
  }, [adapter, onChanged, scopeKind, scopeRefId, submissionId]);

  const patchField = useCallback(
    async (
      adj: AdjustmentWithMonths,
      patch: {
        reason?: string;
        contributionPct?: number | null;
        bookingToInvoiceTimeDays?: number | null;
      },
    ) => {
      markSaving(adj.id, true);
      try {
        await adapter.updateAdjustment(adj.id, patch);
        onChanged?.();
      } finally {
        markSaving(adj.id, false);
      }
    },
    [adapter, markSaving, onChanged],
  );

  const handleDelete = useCallback(
    async (adj: AdjustmentWithMonths) => {
      markSaving(adj.id, true);
      try {
        await adapter.deleteAdjustment(adj.id);
        onChanged?.();
      } finally {
        markSaving(adj.id, false);
      }
    },
    [adapter, markSaving, onChanged],
  );

  // Cross-cell keyboard nav: lets users tab through the 12 monthly cells of a
  // row, jump to the next row's first cell on Enter, and Esc-cancel to the
  // prior value. Keys live on the row's container so we share one listener.
  const onRowKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLDivElement>,
      rowIdx: number,
      totalRows: number,
    ) => {
      const target = e.target as HTMLElement;
      if (!target.matches('input[data-cell-input="true"]')) return;
      const cellIdx = parseInt(target.dataset.cellIdx ?? "-1", 10);
      if (cellIdx < 0) return;

      // A cell not in edit mode renders as a button; focusing it flips it
      // into its input, so either element is a valid Enter target.
      const findCell = (r: number, c: number): HTMLElement | null => {
        const root = gridRef.current;
        if (!root) return null;
        return root.querySelector<HTMLElement>(
          `input[data-cell-input="true"][data-row-idx="${r}"][data-cell-idx="${c}"], ` +
            `[data-cell-button="true"][data-row-idx="${r}"][data-cell-idx="${c}"]`,
        );
      };

      if (e.key === "Enter") {
        e.preventDefault();
        const next = findCell(rowIdx + 1, 0);
        if (next) {
          next.focus();
          if (next instanceof HTMLInputElement) next.select();
        } else {
          target.blur();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        const original = target.dataset.original ?? "";
        (target as HTMLInputElement).value = original;
        target.blur();
      } else if (
        e.key === "Tab" &&
        !e.shiftKey &&
        cellIdx === 11 &&
        rowIdx === totalRows - 1
      ) {
        // Let the browser handle tab elsewhere — but at the very last cell of
        // the last row let it leave the grid naturally.
      }
    },
    [],
  );

  return (
    <div ref={gridRef} className="space-y-2">
      {/* Month header row — shares the label-gutter width with every row
          below so headings and cells always align. */}
      <div className="flex gap-2 px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <div className="w-56 shrink-0" />
        <div className="flex-1 grid grid-cols-12 gap-1">
          {monthLabels.map((m, i) => (
            <div key={i} className="text-center">
              {m}
            </div>
          ))}
        </div>
      </div>

      {adjustments.length === 0 && (
        <p className="text-sm italic text-muted-foreground px-1 py-2">
          No adjustments yet. Click "+ Add adjustment" to add one.
        </p>
      )}

      {adjustments.map((adj, rowIdx) => {
        const saving = savingRowIds.has(adj.id);
        return (
          <div
            key={adj.id}
            data-adj-id={adj.id}
            className="flex gap-2 px-1"
            onKeyDown={(e) => onRowKeyDown(e, rowIdx, adjustments.length)}
          >
            {/* Label gutter — title + margin/lead overrides, all editable
                in place with the same click-to-edit grammar as the cells. */}
            <div className="w-56 shrink-0 min-w-0">
              <InlineTitleCell
                title={adj.reason}
                scopeLabel={scopeLabel}
                onCommit={(reason) => patchField(adj, { reason })}
              />
              <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap text-[10px] text-muted-foreground">
                <InlineOverrideCell
                  label="margin"
                  suffix="%"
                  help="Contribution margin: converts this adjustment's contribution £ into revenue on the Revenue currency views."
                  value={adj.contributionPct}
                  inheritedValue={bucketDefaults?.contributionPct}
                  formatValue={(v) => v.toFixed(1)}
                  parse={(raw) => {
                    const n = parseFloat(raw);
                    return Number.isFinite(n) ? n : null;
                  }}
                  onCommit={(v) => patchField(adj, { contributionPct: v })}
                />
                <span>·</span>
                <InlineOverrideCell
                  label="booked → invoice lead"
                  suffix="d"
                  help="Days between booking and invoicing. Shifts when this adjustment's amounts land on the Invoiced views (amounts are typed by booking month)."
                  value={adj.bookingToInvoiceTimeDays}
                  inheritedValue={bucketDefaults?.bookingToInvoiceDays}
                  formatValue={(v) => String(Math.round(v))}
                  parse={(raw) => {
                    const n = parseInt(raw, 10);
                    return Number.isFinite(n) ? n : null;
                  }}
                  onCommit={(v) =>
                    patchField(adj, { bookingToInvoiceTimeDays: v })
                  }
                />
                {saving ? (
                  <Loader2 className="ml-1 h-3 w-3 animate-spin shrink-0" />
                ) : (
                  <button
                    type="button"
                    aria-label={`Delete adjustment ${adj.reason || "(untitled)"}`}
                    onClick={() => handleDelete(adj)}
                    className="ml-1 shrink-0 rounded p-0.5 text-muted-foreground/50 hover:bg-surface-hover hover:text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            {/* Cell row — the flex-1 12-col grid mirrors the header above. */}
            <div className="flex-1 grid grid-cols-12 gap-1 items-start">
              {FY_MONTHS.map((m, i) => {
                const k = monthKey(m.year, m.month);
                const value = rowsById.get(adj.id)?.get(k) ?? 0;
                return (
                  <AdjustmentCell
                    key={k}
                    rowIdx={rowIdx}
                    cellIdx={i}
                    monthLabel={monthLabels[i]}
                    description={adj.reason || "(untitled)"}
                    value={value}
                    onCommit={(raw) => commitCell(adj, m.year, m.month, raw)}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={handleAddRow}
        disabled={isCreating}
      >
        {isCreating ? "Adding…" : "+ Add adjustment"}
      </Button>
      {createError && (
        <p className="text-xs text-red-600 px-1" role="alert">
          Couldn't add adjustment: {createError}
        </p>
      )}
    </div>
  );
}

// ── Inline title cell ────────────────────────────────────────────
//
// The row's heading, editable in place: a borderless input that reveals
// its affordance on hover/focus. Commit on blur; Enter blurs; Esc
// reverts. An empty commit keeps the prior title (a row must stay
// findable).

function InlineTitleCell({
  title,
  scopeLabel,
  onCommit,
}: {
  title: string;
  scopeLabel?: string;
  onCommit: (reason: string) => void;
}) {
  return (
    <input
      type="text"
      defaultValue={title}
      key={title}
      aria-label={`Adjustment description${scopeLabel ? ` on ${scopeLabel}` : ""}`}
      title={title}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => {
        const next = e.currentTarget.value.trim();
        if (next && next !== title) onCommit(next);
        else e.currentTarget.value = title;
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          e.currentTarget.value = title;
          e.currentTarget.blur();
        }
      }}
      className="h-6 w-full truncate rounded border border-transparent bg-transparent px-1 text-sm font-medium hover:border-border focus:border-primary focus:outline-none"
    />
  );
}

// ── Inline override micro-cell (margin % / lead days) ────────────
//
// Same click-to-edit grammar as the month cells, at meta-line size.
// null = inherit from calibration: shown as the inherited value in a
// lighter tone (or "inherit" when no default is known). Committing an
// empty value reverts to inherit.

function InlineOverrideCell({
  label,
  suffix,
  help,
  value,
  inheritedValue,
  formatValue,
  parse,
  onCommit,
}: {
  label: string;
  suffix: string;
  /** One-sentence explanation of what the field means for the forecast. */
  help: string;
  value: number | null;
  inheritedValue?: number;
  formatValue: (v: number) => string;
  parse: (raw: string) => number | null;
  onCommit: (v: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const isInherited = value === null;
  const display = isInherited
    ? inheritedValue !== undefined
      ? `${formatValue(inheritedValue)}${suffix}`
      : "inherit"
    : `${formatValue(value)}${suffix}`;

  if (editing) {
    return (
      <input
        type="text"
        inputMode="decimal"
        autoFocus
        defaultValue={isInherited ? "" : formatValue(value)}
        aria-label={`${label} override`}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => {
          setEditing(false);
          const raw = e.currentTarget.value.trim();
          const next = raw === "" ? null : parse(raw);
          if (next !== value) onCommit(next);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            e.currentTarget.value = isInherited ? "" : formatValue(value);
            e.currentTarget.blur();
          }
        }}
        className="h-4 w-12 whitespace-nowrap rounded border border-border bg-background px-0.5 text-center text-[10px] tabular-nums focus:border-primary focus:outline-none"
      />
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={`${label}: ${display}${isInherited ? " (inherited from calibration)" : " (override)"}`}
          className={`whitespace-nowrap rounded border border-transparent px-0.5 text-[10px] font-normal tabular-nums hover:border-border hover:bg-surface-hover ${
            isInherited ? "text-muted-foreground/60" : "text-foreground"
          }`}
        >
          {label} {display}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-left normal-case">
        {help}{" "}
        {isInherited
          ? "Currently inherited from the bucket's calibration — click to override."
          : "Overridden for this adjustment — clear the value to revert to the calibrated default."}
      </TooltipContent>
    </Tooltip>
  );
}

// ── Per-cell input ───────────────────────────────────────────────
//
// Cells render a formatted £k display when not focused, and switch to a
// raw-number text input on focus so users can type quickly. Commit semantics:
//   Tab / Shift+Tab — move between cells (browser default), commits via blur.
//   Enter — advances focus to the next row's first cell (handled by parent).
//   Esc — restores the pre-edit value and blurs without committing.

interface CellProps {
  rowIdx: number;
  cellIdx: number;
  monthLabel: string;
  description: string;
  value: number;
  onCommit: (raw: string) => void;
}

export function AdjustmentCell({
  rowIdx,
  cellIdx,
  monthLabel,
  description,
  value,
  onCommit,
}: CellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>("");

  if (editing) {
    return (
      <input
        type="text"
        inputMode="text"
        data-cell-input="true"
        data-row-idx={rowIdx}
        data-cell-idx={cellIdx}
        data-original={value === 0 ? "" : String(value)}
        autoFocus
        defaultValue={value === 0 ? "" : String(value)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          setEditing(false);
          onCommit(e.currentTarget.value);
        }}
        onFocus={(e) => e.currentTarget.select()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Parent's onKeyDown handles row advance; commit via blur.
            (e.currentTarget as HTMLInputElement).blur();
          }
        }}
        aria-label={`${monthLabel} adjustment for ${description}`}
        className="h-7 w-full rounded border border-border bg-background px-1 text-center text-xs tabular-nums focus:border-primary focus:outline-none"
      />
    );
  }
  void draft;

  return (
    <button
      type="button"
      data-cell-button="true"
      data-row-idx={rowIdx}
      data-cell-idx={cellIdx}
      onClick={() => setEditing(true)}
      onFocus={() => setEditing(true)}
      aria-label={`${monthLabel} adjustment for ${description}, currently ${fmtCellDisplay(value)}`}
      className={`h-7 w-full rounded border border-transparent px-1 text-center text-xs tabular-nums hover:border-border hover:bg-surface-hover ${
        value === 0 ? "text-muted-foreground/60" : "font-medium"
      }`}
    >
      {fmtCellDisplay(value)}
    </button>
  );
}

// ── Per-row edit popover ─────────────────────────────────────────
//
// No longer used by AdjustmentGrid itself (title + overrides edit
// inline in the row now) — still consumed by BucketsAdjustmentTable,
// whose table layout has no label gutter to host inline editors.

interface EditPopoverProps {
  adj: AdjustmentWithMonths;
  bucketDefaults?: { contributionPct?: number; bookingToInvoiceDays?: number };
  onSave: (patch: {
    reason: string;
    contributionPct: number | null;
    bookingToInvoiceTimeDays: number | null;
  }) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
}

export function AdjustmentRowEditPopover({
  adj,
  bucketDefaults,
  onSave,
  onDelete,
}: EditPopoverProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(adj.reason);
  const [contrib, setContrib] = useState(
    adj.contributionPct !== null ? String(adj.contributionPct) : "",
  );
  const [lead, setLead] = useState(
    adj.bookingToInvoiceTimeDays !== null
      ? String(adj.bookingToInvoiceTimeDays)
      : "",
  );

  // Reset local state when the popover opens so it always reflects the
  // current canonical row, not stale prior edits.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setReason(adj.reason);
      setContrib(
        adj.contributionPct !== null ? String(adj.contributionPct) : "",
      );
      setLead(
        adj.bookingToInvoiceTimeDays !== null
          ? String(adj.bookingToInvoiceTimeDays)
          : "",
      );
    }
    setOpen(next);
  };

  const handleSave = async () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    const contribNum = contrib === "" ? null : parseFloat(contrib);
    const leadNum = lead === "" ? null : parseInt(lead, 10);
    await onSave({
      reason: trimmed,
      contributionPct:
        contribNum !== null && Number.isFinite(contribNum) ? contribNum : null,
      bookingToInvoiceTimeDays:
        leadNum !== null && Number.isFinite(leadNum) ? leadNum : null,
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Edit adjustment overrides"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="space-y-1">
          <Label htmlFor={`adj-edit-reason-${adj.id}`} className="text-xs">
            Description
          </Label>
          <Input
            id={`adj-edit-reason-${adj.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="h-8"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`adj-edit-contrib-${adj.id}`} className="text-xs">
            Contribution %
          </Label>
          <Input
            id={`adj-edit-contrib-${adj.id}`}
            type="number"
            value={contrib}
            onChange={(e) => setContrib(e.target.value)}
            placeholder={
              bucketDefaults?.contributionPct !== undefined
                ? `${bucketDefaults.contributionPct.toFixed(1)} (inherit)`
                : "inherit from calibration"
            }
            className="h-8"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`adj-edit-lead-${adj.id}`} className="text-xs">
            Booking-to-invoice days
          </Label>
          <Input
            id={`adj-edit-lead-${adj.id}`}
            type="number"
            value={lead}
            onChange={(e) => setLead(e.target.value)}
            placeholder={
              bucketDefaults?.bookingToInvoiceDays !== undefined
                ? `${bucketDefaults.bookingToInvoiceDays} (inherit)`
                : "inherit from calibration"
            }
            className="h-8"
          />
        </div>
        <div className="flex items-center justify-between pt-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 hover:text-red-700"
            onClick={async () => {
              await onDelete();
              setOpen(false);
            }}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!reason.trim()}>
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
